import React, { useRef, useEffect, useState } from "react";
import { storage } from "../../firebaseConfig";
import { ref, getDownloadURL } from "firebase/storage";

const VideoPlayer = ({
  metadata,
  selectedVideoId,
  chatData,
  setAllShownMessages,
  setDisplayedMessages,
  intervals,
  onIntervalChange,
}) => {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [currentIntervalIndex, setCurrentIntervalIndex] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showPauseOverlay, setShowPauseOverlay] = useState(false);
  const [currentPauseInfo, setCurrentPauseInfo] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [initialTimeSet, setInitialTimeSet] = useState(false);
  const [processedPauses, setProcessedPauses] = useState(new Set());

  useEffect(() => {
    if (!metadata || !videoRef.current) return;

    const loadVideo = async () => {
      try {
        const extractPathFromURL = (url) => {
          if (!url) throw new Error("URL is null or undefined");
          const parts = url.split(".appspot.com/");
          if (parts.length < 2)
            throw new Error("Invalid URL format for Firebase Storage");
          const pathWithParams = parts[1];
          return pathWithParams.split("?")[0];
        };

        if (!metadata.videoURL) throw new Error("No videoURL in metadata");

        const videoPath = extractPathFromURL(metadata.videoURL);
        const videoStorageRef = ref(storage, videoPath);
        const downloadURL = await getDownloadURL(videoStorageRef);

        if (videoRef.current) {
          videoRef.current.src = downloadURL;
          videoRef.current.load();
          setVideoLoaded(true);
          setInitialTimeSet(false);
        }
      } catch (err) {
        console.error("Error loading video:", err);
        try {
          if (videoRef.current) {
            videoRef.current.src = metadata.videoURL;
            videoRef.current.load();
            setVideoLoaded(true);
            setInitialTimeSet(false);
          }
        } catch {
          setError(`Error loading video: ${err.message}`);
        }
      }
    };

    loadVideo();
  }, [metadata, selectedVideoId]);

  useEffect(() => {
    if (
      videoLoaded &&
      intervals &&
      intervals.length > 0 &&
      videoRef.current &&
      !initialTimeSet
    ) {
      console.log("=== INTERVALS DEBUG ===");
      console.log("All intervals:", JSON.stringify(intervals, null, 2));
      console.log("First interval:", intervals[0]);
      console.log("First interval pauses:", intervals[0].pauses);
      console.log("Setting initial time for first interval:", intervals[0]);
      videoRef.current.currentTime = intervals[0].startTime;
      setInitialTimeSet(true);
      if (onIntervalChange) {
        onIntervalChange(0, intervals[0]);
      }
    }
  }, [videoLoaded, intervals, onIntervalChange, initialTimeSet]);

  useEffect(() => {
    setInitialTimeSet(false);
    setCurrentIntervalIndex(0);
    setProcessedPauses(new Set());
  }, [selectedVideoId, intervals]);

  // to check for pauses with an array
  const checkForPause = (currentTime, currentInterval) => {
    if (
      !currentInterval ||
      !currentInterval.pauses ||
      !Array.isArray(currentInterval.pauses)
    ) {
      console.log("No pauses array in current interval:", currentInterval);
      return null;
    }

    // check if the time is within the current interval
    if (
      currentTime < currentInterval.startTime ||
      currentTime > currentInterval.endTime
    ) {
      return null;
    }

    console.log(
      "Checking pauses for time:",
      currentTime,
      "in interval:",
      currentInterval,
    );
    console.log("Available pauses:", currentInterval.pauses);

    for (let i = 0; i < currentInterval.pauses.length; i++) {
      const pauseTime = currentInterval.pauses[i];

      if (pauseTime === null || pauseTime === undefined || isNaN(pauseTime)) {
        console.log(`Skipping invalid pause at index ${i}: ${pauseTime}`);
        continue;
      }

      const numPauseTime = Number(pauseTime);
      const pauseId = `${currentIntervalIndex}-${i}-${numPauseTime}`;
      const timeDiff = Math.abs(currentTime - numPauseTime);

      console.log(
        `Checking pause ${i}: time=${numPauseTime}, currentTime=${currentTime}, diff=${timeDiff}, processed=${processedPauses.has(pauseId)}`,
      );

      // check if the current time has reached the pause and if it has not yet been processed
      if (timeDiff < 0.5 && !processedPauses.has(pauseId)) {
        console.log("PAUSE TRIGGERED at index", i, "at time", numPauseTime);
        return {
          time: numPauseTime,
          index: i,
          id: pauseId,
        };
      }
    }
    return null;
  };

  const handleTimeUpdate = () => {
    if (
      !videoRef.current ||
      chatData.length === 0 ||
      !intervals ||
      intervals.length === 0
    )
      return;

    const currentTime = videoRef.current.currentTime;
    const currentInterval = intervals[currentIntervalIndex];

    if (
      Math.floor(currentTime) % 5 === 0 &&
      Math.floor(currentTime * 10) % 50 === 0
    ) {
      console.log(
        "Time update:",
        currentTime,
        "Interval:",
        currentIntervalIndex,
        "InitialTimeSet:",
        initialTimeSet,
      );
    }

    // check if a pause is needed (only if the video has already been set up)
    if (initialTimeSet) {
      const pauseInfo = checkForPause(currentTime, currentInterval);
      if (pauseInfo && !showPauseOverlay) {
        console.log("PAUSE ACTIVATED:", pauseInfo);
        setProcessedPauses((prev) => new Set(prev).add(pauseInfo.id));
        videoRef.current.pause();
        setShowPauseOverlay(true);
        setCurrentPauseInfo(pauseInfo);
        return;
      }
    }

    // if you have reached the end of the current interval
    if (currentTime >= currentInterval.endTime) {
      videoRef.current.pause();
      setShowOverlay(true);
    }

    // chat synchronization
    const messagesToShow = chatData.filter(
      (m) => m.time_in_seconds <= currentTime,
    );
    setAllShownMessages(messagesToShow);
    setDisplayedMessages(messagesToShow.slice(-50));
  };

  const handleContinue = () => {
    const nextIndex = currentIntervalIndex + 1;
    if (nextIndex < intervals.length) {
      setCurrentIntervalIndex(nextIndex);
      setShowOverlay(false);
      // reset the processed pauses for the new interval
      setProcessedPauses(new Set());
      if (videoRef.current) {
        videoRef.current.currentTime = intervals[nextIndex].startTime;
        videoRef.current.play();
        if (onIntervalChange) {
          onIntervalChange(nextIndex, intervals[nextIndex]);
        }
      }
    } else {
      alert("🎉 You have viewed all available intervals!");
      setShowOverlay(false);
    }
  };

  const handleContinueFromPause = () => {
    setShowPauseOverlay(false);
    setCurrentPauseInfo(null);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (error) {
    return (
      <div className="video-player-container">
        <div className="bg-red-900 rounded-lg p-4 w-[800px] h-[450px] flex items-center justify-center">
          <div className="text-white text-center">
            <h3 className="text-lg font-semibold mb-2">Error loading video</h3>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!intervals || intervals.length === 0) {
    return (
      <div className="video-player-container">
        <div className="bg-gray-900 rounded-lg p-4 w-[800px] h-[450px] flex items-center justify-center">
          <div className="text-white text-center">
            <h3 className="text-lg font-semibold mb-2">
              No playback intervals
            </h3>
            <p className="text-sm">Configure intervals in the admin panel</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-player-container relative mt-6">
      <div className="bg-black rounded-lg overflow-hidden w-[800px] h-[450px] relative">
        <video
          ref={videoRef}
          className="w-full h-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          controls
          crossOrigin="anonymous"
          muted
        >
          Your browser does not support the video element.
        </video>

        {/* у */}
        {showOverlay && (
          <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center text-white z-10">
            <div className="text-center mb-6">
              <p className="mb-2 text-lg">Interval completed</p>
              <p className="mb-2 text-lg">Wait for further instructions</p>
              <a
                className="mb-2 text-lg text-blue-400 underline"
                href="https://uoguelph.eu.qualtrics.com/jfe/form/SV_8eq7o68P7Yuh8yy"
                target="_blank"
              >
                Post interval questions!
              </a>
            </div>
            <button
              onClick={handleContinue}
              className="px-6 py-3 bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
            >
              {currentIntervalIndex + 1 < intervals.length
                ? "Continue watching"
                : "Finish viewing"}
            </button>
          </div>
        )}

        {/*  */}
        {showPauseOverlay && (
          <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center text-white z-10">
            <div className="text-center mb-6">
              <p className="mb-2 text-lg">Video paused</p>
              <p className="mb-2 text-lg">Wait for further instructions</p>
              <p className="text-sm text-gray-300"></p>
            </div>
            <button
              onClick={handleContinueFromPause}
              className="px-6 py-3 bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
            >
              Continue watching
            </button>
          </div>
        )}
      </div>

      {metadata && (
        <div className="mt-6 max-w-[800px]">
          <h2 className="text-xl font-semibold mb-2">{metadata.title}</h2>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
